import { useCallback, useEffect, useReducer, useState } from "react";

import type {
  Campaign,
  CreateMarketingBannerInputBody,
  CreateMarketingBannerInputBodyType,
  OfferProgram,
} from "../../api/generated";
import type { SelectV3Option } from "../../SelectV3";
import type { MarketingBannerRow, MarketingBannerType } from "../types";
import type {
  MarketingBannerAsset,
  MarketingBannerConfigurationActions,
  MarketingBannerConfigurationValues,
  MarketingBannerFormat,
  MarketingBannerRebateProgramOption,
  MarketingBannerSize,
} from "./types";

import { closestToSquare } from "../selectThumbnailAsset";
import { DEFAULT_BANNER_SIZE } from "../types";

type MarketingBannerReducerState = MarketingBannerConfigurationValues & {
  status: "idle" | "duplicateName" | "duplicateBanner" | "error";
  bannerOptions: MarketingBannerRow[];
  sizes: MarketingBannerSize[];
  languageOptions: SelectV3Option[];
  // Id of the existing banner a duplicate create collided with (from the 409
  // response). Lets "View banner" deep-link to that row. Set only alongside
  // status "duplicateBanner".
  duplicateBannerId?: string;
};

type CreativeSelection = {
  format: MarketingBannerFormat;
  bannerOptions: MarketingBannerRow[];
  sizes: MarketingBannerSize[];
  size: MarketingBannerSize | undefined;
  languageOptions: SelectV3Option[];
  language: SelectV3Option | undefined;
  name: string;
};

type CreativeSelectionInput = {
  assets: MarketingBannerAsset[] | undefined;
  format: MarketingBannerFormat;
  preferredSize: MarketingBannerSize | undefined;
  preferredLanguage: SelectV3Option | undefined;
  bannerGroup: string;
  title: string | undefined;
  getLanguageLabelFromCode: (code: string) => string;
  fallbackToFirstSize: boolean;
};

type MarketingBannerConfigurationAction =
  | { type: "APPLY_CREATIVE_SELECTION"; payload: CreativeSelection }
  | { type: "SET_LANGUAGE"; payload: SelectV3Option | undefined }
  | {
      type: "SET_REBATE_PROGRAM";
      payload: MarketingBannerRebateProgramOption | undefined;
    }
  | { type: "SET_CAMPAIGN"; payload: Campaign | undefined }
  | { type: "SET_NAME"; payload: string }
  | {
      type: "SET_STATUS";
      payload: "idle" | "duplicateName" | "duplicateBanner" | "error";
    }
  | { type: "SET_DUPLICATE_BANNER"; payload: { id?: string } }
  | { type: "RESET"; payload: MarketingBannerReducerState };

// Pull the existing banner's id out of a 409 error. useFetchWrapper attaches the
// parsed problem+json body as `error.body`; the backend puts the id in
// errors[0].value. Defensive about shape since `body` is typed unknown.
const extractDuplicateBannerId = (err: unknown): string | undefined => {
  const body = (err as { body?: unknown } | null)?.body;
  if (!body || typeof body !== "object") return undefined;
  const errorsList = (body as { errors?: unknown }).errors;
  if (!Array.isArray(errorsList) || errorsList.length === 0) return undefined;
  const value = (errorsList[0] as { value?: unknown })?.value;
  return typeof value === "string" && value !== "" ? value : undefined;
};

const NAME_MAX_LENGTH = 60;

const formatName = (
  title: string | undefined,
  size: MarketingBannerSize | undefined,
  language: SelectV3Option | undefined,
) => {
  const parts = [title, size?.label, language?.label].filter(Boolean);
  return parts.join(" - ").slice(0, NAME_MAX_LENGTH);
};

const compareRebatePrograms = (
  a: { hoid: string; currency: string; rebateAccount: string },
  b: { hoid: string; currency: string; rebateAccount: string },
) =>
  a.hoid.localeCompare(b.hoid, undefined, { numeric: true }) ||
  a.currency.localeCompare(b.currency) ||
  a.rebateAccount.localeCompare(b.rebateAccount, undefined, {
    numeric: true,
  });

const formatType = (format: MarketingBannerFormat | undefined): string =>
  format === "image" ? "image_banner" : "html_ad";

const deriveSizesFromAssets = (
  assets: MarketingBannerAsset[] | undefined,
  format: MarketingBannerFormat | undefined,
): MarketingBannerSize[] => {
  const type = formatType(format);
  const seen = new Map<string, MarketingBannerSize>();
  for (const a of assets ?? []) {
    if (a.type !== type) continue;
    const key = `${a.width}x${a.height}`;
    if (!seen.has(key)) {
      seen.set(key, {
        label: `${a.width}×${a.height}`,
        width: a.width,
        height: a.height,
      });
    }
  }
  return [...seen.values()];
};

const deriveLanguagesFromAssets = (
  assets: MarketingBannerAsset[] | undefined,
  format: MarketingBannerFormat | undefined,
  size?: MarketingBannerSize,
): string[] => {
  const type = formatType(format);
  return [
    ...new Set(
      (assets ?? [])
        .filter((a) => a.type === type)
        .filter(
          (a) => !size || (a.width === size.width && a.height === size.height),
        )
        .map((a) => a.language),
    ),
  ];
};

const selectAvailableLanguage = (
  languageOptions: SelectV3Option[],
  preferredLanguage: SelectV3Option | undefined,
): SelectV3Option | undefined => {
  return preferredLanguage
    ? (languageOptions.find(
        (option) => option.value === preferredLanguage.value,
      ) ?? languageOptions[0])
    : languageOptions[0];
};

const toLanguageOptions = (
  languages: string[],
  getLanguageLabelFromCode: (code: string) => string,
): SelectV3Option[] => {
  return languages
    .map((language) => ({
      label: getLanguageLabelFromCode(language),
      value: language,
    }))
    .sort((a, b) => {
      // Sort English first (by code, so it holds across locales), then the
      // rest alphabetically by translated label.
      if (a.value === "en") return -1;
      if (b.value === "en") return 1;
      return a.label.localeCompare(b.label);
    });
};

// Whether an asset exists for the given size + language (+ format type).
// Used to disable size options that have no creative in the selected language.
export const sizeHasAssetForLanguage = (
  assets: MarketingBannerAsset[] | undefined,
  format: MarketingBannerFormat | undefined,
  size: MarketingBannerSize,
  language: string,
): boolean => {
  const type = formatType(format);
  return (assets ?? []).some(
    (a) =>
      a.type === type &&
      a.width === size.width &&
      a.height === size.height &&
      a.language === language,
  );
};

const deriveBannerOptionsFromAssets = (
  assets: MarketingBannerAsset[] | undefined,
  format: MarketingBannerFormat | undefined,
  bannerGroup: string,
): MarketingBannerRow[] => {
  const type = formatType(format);
  // Keep one option per size + language so the preview can match a creative by
  // width, height AND language (each asset is language-specific).
  const bySizeLanguage = new Map<string, MarketingBannerAsset>();
  for (const a of assets ?? []) {
    if (a.type !== type) continue;
    const key = `${a.width}x${a.height}-${a.language}`;
    if (!bySizeLanguage.has(key)) bySizeLanguage.set(key, a);
  }
  return [...bySizeLanguage.values()].map((a) => ({
    id: a.id,
    name: `${a.width}x${a.height}`,
    creative_code:
      a.type === "image_banner" ? (a.thumbnail ?? "") : (a.code ?? ""),
    offer_id: "",
    creative_file_id: String(a.fileId),
    creative_file_name: a.fileName,
    type: a.type as MarketingBannerType,
    banner_group: bannerGroup,
    width: a.width,
    height: a.height,
    language: a.language,
    hoid: "",
    created_at: "",
  }));
};

export const deriveCreativeSelection = ({
  assets,
  format,
  preferredSize,
  preferredLanguage,
  bannerGroup,
  title,
  getLanguageLabelFromCode,
  fallbackToFirstSize,
}: CreativeSelectionInput): CreativeSelection => {
  const sizes = deriveSizesFromAssets(assets, format);
  const bannerOptions = deriveBannerOptionsFromAssets(
    assets,
    format,
    bannerGroup,
  );
  const preferredSizeAvailable = sizes.some(
    (size) =>
      size.width === preferredSize?.width &&
      size.height === preferredSize?.height,
  );
  const size = preferredSizeAvailable
    ? preferredSize
    : fallbackToFirstSize
      ? // No preferred size: prefer the default 300×250, otherwise the size
        // closest to square so the modal's initial preview matches the (also
        // square-biased) library thumbnail. `sizes[0]` guards the edge case
        // where every size has a non-positive dimension.
        (sizes.find(
          (s) =>
            s.width === DEFAULT_BANNER_SIZE.width &&
            s.height === DEFAULT_BANNER_SIZE.height,
        ) ??
        closestToSquare(sizes) ??
        sizes[0])
      : undefined;
  const languageOptions = toLanguageOptions(
    deriveLanguagesFromAssets(assets, format, size),
    getLanguageLabelFromCode,
  );
  const language = selectAvailableLanguage(languageOptions, preferredLanguage);

  return {
    format,
    bannerOptions,
    sizes,
    size,
    languageOptions,
    language,
    name: formatName(title, size, language),
  };
};

const initialState: MarketingBannerReducerState = {
  name: "",
  status: "idle",
  bannerOptions: [],
  sizes: [],
  languageOptions: [],
  banner: undefined,
};

const reducer = (
  state: MarketingBannerReducerState,
  action: MarketingBannerConfigurationAction,
): MarketingBannerReducerState => {
  switch (action.type) {
    case "APPLY_CREATIVE_SELECTION": {
      return {
        ...state,
        // Clear any validation error/warning from a previous submit so the
        // submit button re-enables once the user changes the form.
        status: "idle",
        ...action.payload,
      };
    }
    case "SET_LANGUAGE":
      return {
        ...state,
        status: "idle",
        language: action.payload,
        name: formatName(state.title, state.size, action.payload),
      };
    case "SET_REBATE_PROGRAM":
      return { ...state, status: "idle", rebateProgram: action.payload };
    case "SET_CAMPAIGN":
      return { ...state, status: "idle", campaign: action.payload };
    case "SET_NAME":
      return { ...state, status: "idle", name: action.payload };
    case "SET_STATUS":
      // Clear the stored duplicate id unless we're (re)entering duplicateBanner,
      // so the id can only ever accompany a duplicateBanner status.
      return {
        ...state,
        status: action.payload,
        duplicateBannerId:
          action.payload === "duplicateBanner"
            ? state.duplicateBannerId
            : undefined,
      };
    case "SET_DUPLICATE_BANNER":
      return {
        ...state,
        status: "duplicateBanner",
        duplicateBannerId: action.payload.id,
      };
    case "RESET":
      // Replace state from the payload outright (no `...state`) so a fresh open
      // starts clean. Spreading the stale state would let fields absent from the
      // payload - e.g. a previously-selected language - leak across opens.
      return {
        ...action.payload,
        status: "idle",
      };
    default:
      return state;
  }
};

export const useMarketingBannerConfigurationData = ({
  isOpen,
  title,
  initialValues,
  actions,
  rebateAccountLabel,
  getLanguageLabelFromCode,
}: {
  isOpen: boolean;
  title?: string;
  initialValues?: Partial<MarketingBannerConfigurationValues>;
  actions: MarketingBannerConfigurationActions;
  /** Builds the rebate-account sub-label (injected, was an i18n call). */
  rebateAccountLabel: (account: string) => string;
  getLanguageLabelFromCode: (code: string) => string;
}) => {
  const seededState: MarketingBannerReducerState = {
    ...initialState,
    ...initialValues,
    title,
    size: initialValues?.size,
    format: initialValues?.format ?? "html",
    name: formatName(title, initialValues?.size, initialValues?.language),
    bannerOptions: [],
    sizes: [],
    languageOptions: [],
  };

  const [values, dispatch] = useReducer(reducer, seededState);

  const setFormat = (format: MarketingBannerFormat) => {
    dispatch({
      type: "APPLY_CREATIVE_SELECTION",
      payload: deriveCreativeSelection({
        assets: values.assets,
        format,
        preferredSize: values.size,
        preferredLanguage: values.language,
        bannerGroup: values.banner?.banner_group ?? "",
        title: values.title,
        getLanguageLabelFromCode,
        fallbackToFirstSize: true,
      }),
    });
  };

  const setSize = (size: MarketingBannerSize | undefined) => {
    dispatch({
      type: "APPLY_CREATIVE_SELECTION",
      payload: deriveCreativeSelection({
        assets: values.assets,
        format: values.format ?? "html",
        preferredSize: size,
        preferredLanguage: values.language,
        bannerGroup: values.banner?.banner_group ?? "",
        title: values.title,
        getLanguageLabelFromCode,
        fallbackToFirstSize: false,
      }),
    });
  };

  const setLanguage = (language: SelectV3Option | undefined) =>
    dispatch({ type: "SET_LANGUAGE", payload: language });
  const setRebateProgram = (
    rebateProgram: MarketingBannerRebateProgramOption | undefined,
  ) => dispatch({ type: "SET_REBATE_PROGRAM", payload: rebateProgram });
  const setCampaign = (campaign: Campaign | undefined) =>
    dispatch({ type: "SET_CAMPAIGN", payload: campaign });
  const setName = (name: string) =>
    dispatch({ type: "SET_NAME", payload: name });
  const setStatus = (
    status: "idle" | "duplicateName" | "duplicateBanner" | "error",
  ) => dispatch({ type: "SET_STATUS", payload: status });
  const setDuplicateBanner = (id?: string) =>
    dispatch({ type: "SET_DUPLICATE_BANNER", payload: { id } });

  const reset = (overrides?: Partial<MarketingBannerConfigurationValues>) => {
    const mergedInitialValues = {
      ...initialValues,
      ...overrides,
    };

    const fmt = mergedInitialValues.format ?? "html";
    const bannerGroup = mergedInitialValues.banner?.banner_group ?? "";
    const mergedTitle = mergedInitialValues.title ?? title;

    const creativeSelection = deriveCreativeSelection({
      assets: mergedInitialValues.assets,
      format: fmt,
      preferredSize: mergedInitialValues.size,
      preferredLanguage: mergedInitialValues.language,
      bannerGroup,
      title: mergedTitle,
      getLanguageLabelFromCode,
      fallbackToFirstSize: true,
    });

    dispatch({
      type: "RESET",
      payload: {
        ...initialState,
        ...mergedInitialValues,
        title: mergedTitle,
        ...creativeSelection,
      },
    });
  };

  // ─── User offer ids (was useGetUserOfferIds) ────────────────────────────────
  const [offerIds, setOfferIds] = useState<OfferProgram[]>([]);
  const [isGettingOfferIds, setIsGettingOfferIds] = useState(true);
  const [offerIdsFailed, setOfferIdsFailed] = useState(false);

  const { fetchUserOfferIds, createMarketingBanner } = actions;

  const loadOfferIds = useCallback(async () => {
    setOfferIdsFailed(false);
    setIsGettingOfferIds(true);
    try {
      const programs = await fetchUserOfferIds();
      setOfferIds(programs ?? []);
    } catch {
      setOfferIds([]);
      setOfferIdsFailed(true);
    } finally {
      setIsGettingOfferIds(false);
    }
  }, [fetchUserOfferIds]);

  useEffect(() => {
    if (!isOpen) return;
    void loadOfferIds();
  }, [isOpen, loadOfferIds]);

  const rebateProgramOptions: MarketingBannerRebateProgramOption[] = [
    ...(offerIds ?? []),
  ]
    .sort(compareRebatePrograms)
    .map((id) => ({
      label: `HOID ${id.hoid} · ${id.currency}`,
      value: `${id.hoid}-${id.rebateAccount}-${id.currency}`,
      hoid: id.hoid,
      rebateAccount: id.rebateAccount,
      currency: id.currency,
      description: rebateAccountLabel(id.rebateAccount),
    }));

  // A non-idle status means the previous submit produced a blocking outcome -
  // a duplicate name (422), a duplicate banner (409), or a generic error. Keep
  // the button disabled until the user edits the form, which resets status to
  // "idle".
  const isBlockedByLastSubmit = values.status !== "idle";

  const [isCreating, setIsCreating] = useState(false);

  const canSubmit =
    !!values.size &&
    !!values.language &&
    !!values.rebateProgram &&
    values.name.trim().length > 0 &&
    !isBlockedByLastSubmit;

  const onCreate = async (payload: MarketingBannerConfigurationValues) => {
    if (!canSubmit) return false;

    const {
      size,
      language,
      rebateProgram,
      campaign,
      banner,
      assets: payloadAssets,
    } = payload;
    if (!size || !language || !rebateProgram || !banner || !payload.offerId)
      return false;

    const expectedType = formatType(payload.format);

    const matchedAsset = payloadAssets?.find(
      (a) =>
        a.width === size.width &&
        a.height === size.height &&
        a.language === language.value &&
        a.type === expectedType,
    );
    if (!matchedAsset) return false;

    const data: CreateMarketingBannerInputBody = {
      HOID: rebateProgram.hoid,
      bannerGroup: banner.banner_group,
      creativeFileID: String(matchedAsset.fileId),
      creativeFileName: matchedAsset.fileName,
      height: size.height,
      language: language.value,
      name: payload.name,
      offerID: String(payload.offerId),
      type: expectedType as CreateMarketingBannerInputBodyType,
      width: size.width,
      campaignID: campaign?.id,
    };

    setIsCreating(true);
    try {
      const result = await createMarketingBanner(data);
      if (result.creativeCode) {
        await navigator.clipboard.writeText(result.creativeCode);
      }
      setStatus("idle");
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("Status: 422")) {
        setStatus("duplicateName");
      } else if (message.includes("Status: 409")) {
        // The 409 body carries the existing banner's id in errors[].value so the
        // user can "View banner" and deep-link to it.
        setDuplicateBanner(extractDuplicateBannerId(err));
      } else {
        setStatus("error");
      }
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  // Auto-select the first rebate program when options arrive asynchronously
  // while the modal is already open. The open-reset effect in the modal fires
  // before the offer ids load, so rebateProgramOptions[0] is undefined at that
  // point and rebateProgram is left unselected. This effect catches that case
  // without overwriting a deliberate user selection.
  useEffect(() => {
    if (
      !isOpen ||
      rebateProgramOptions.length === 0 ||
      values.rebateProgram !== undefined
    )
      return;
    dispatch({ type: "SET_REBATE_PROGRAM", payload: rebateProgramOptions[0] });
    // rebateProgramOptions is derived synchronously from offerIds; using
    // offerIds as the dependency avoids a new reference on every render while
    // still re-running whenever the fetched data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, offerIds]);

  return {
    values,
    setFormat,
    setSize,
    setLanguage,
    setRebateProgram,
    setCampaign,
    setName,
    reset,
    canSubmit,
    offerIds,
    isGettingOfferIds,
    rebateProgramOptions,
    isError: offerIdsFailed,
    retry: loadOfferIds,
    onCreate,
    isCreating,
  };
};
